import React, { useState, useEffect } from 'react';
import {
  Shield, Users, Building2, FileText, CheckCircle, XCircle,
  Plus, Edit, Trash2, Eye, EyeOff, Search, Filter, RefreshCw,
  ChevronDown, ChevronUp, Mail, Phone, MapPin, Globe, Clock, AlertCircle,
  LogIn, LogOut, User, Lock, UserPlus, Settings, Home, ArrowLeft,
  BarChart3, Image, Target, Bell, CreditCard, Receipt, DollarSign, Upload,
  Store, Play, AlertTriangle, MessageSquare,
  GraduationCap, School, Award, CloudRain, Star, Briefcase
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db, isSupabaseConfigured, contactSubmissions, educationJobs } from '../lib/supabase';
import supabase from '../lib/supabase';

// Jamaica Parishes for dropdown
const JAMAICA_PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// Tier options
const TIER_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' }
];

// ==================== LOGIN PAGE ====================
export const LoginPage = ({ setPage }) => {
  const { signIn, signUp, isSupabaseConfigured: configured } = useAuth();
  const [mode, setMode] = useState('login'); // login, register, forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('parent'); // parent or service_provider
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  if (!configured) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-amber-900">Backend Not Configured</h2>
          <p className="text-amber-700 mt-2">
            Supabase credentials are not set up. The app is running in offline mode.
          </p>
          <p className="text-sm text-amber-600 mt-4">
            Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to your environment variables.
          </p>
          <button onClick={() => setPage('home')} className="mt-6 px-6 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (mode === 'register') {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }
        const { error } = await signUp(email, password, fullName, userType);
        if (error) throw error;
        if (userType === 'service_provider') {
          setSuccess('Registration successful! Redirecting to Get Listed form...');
          setTimeout(() => setPage('getlisted'), 1500);
        } else {
          setSuccess('Registration successful! Check your email to verify your account.');
          setMode('login');
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) throw error;
        setPage('home');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center py-12">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-brand-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {mode === 'register' ? <UserPlus className="w-8 h-8 text-brand-600" /> : <LogIn className="w-8 h-8 text-brand-600" />}
          </div>
          <h2 className="text-2xl font-black">{mode === 'register' ? 'Create Account' : 'Welcome Back'}</h2>
          <p className="text-gray-500 mt-1">{mode === 'register' ? 'Sign up to save your data' : 'Sign in to your account'}</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />{error}
          </div>
        )}

        {success && (
          <div className="bg-brand-50 border border-brand-200 text-brand-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />{success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">I am a...</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setUserType('parent')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      userType === 'parent'
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <User className="w-6 h-6 mb-1" />
                    <p className="font-bold">Parent/Student</p>
                    <p className="text-xs text-gray-500">Exploring schools</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setUserType('service_provider')}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      userType === 'service_provider'
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Building2 className="w-6 h-6 mb-1" />
                    <p className="font-bold">Education Provider</p>
                    <p className="text-xs text-gray-500">Tutor or service</p>
                  </button>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
              placeholder="your@email.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
              placeholder="••••••••"
              required
            />
          </div>

          {mode === 'register' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                placeholder="••••••••"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <><RefreshCw className="w-5 h-5 animate-spin" />Processing...</>
            ) : mode === 'register' ? (
              <><UserPlus className="w-5 h-5" />Create Account</>
            ) : (
              <><LogIn className="w-5 h-5" />Sign In</>
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          {mode === 'login' ? (
            <p className="text-gray-600">
              Don't have an account?{' '}
              <button onClick={() => setMode('register')} className="text-brand-600 font-bold hover:underline">
                Sign up
              </button>
            </p>
          ) : (
            <p className="text-gray-600">
              Already have an account?{' '}
              <button onClick={() => setMode('login')} className="text-brand-600 font-bold hover:underline">
                Sign in
              </button>
            </p>
          )}
        </div>

        <button
          onClick={() => setPage('home')}
          className="mt-4 w-full py-3 text-gray-600 font-medium hover:text-gray-900 flex items-center justify-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />Back to Home
        </button>
      </div>
    </div>
  );
};

// ==================== ADMIN DASHBOARD ====================
export const AdminDashboard = ({ setPage }) => {
  const { profile, signOut } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [pendingJobs, setPendingJobs] = useState(0);
  const [revenueData, setRevenueData] = useState({
    listingRevenue: 0,
    bannerRevenue: 0,
    pendingListingRequests: 0,
    pendingBannerRequests: 0
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [statsRes, subsRes, bannerRes, paymentReqRes] = await Promise.all([
        db.stats.getDashboard(),
        db.submissions.getAll('pending'),
        supabase.from('banner_ad_requests').select('*'),
        supabase.from('payment_requests').select('*')
      ]);
      setStats(statsRes.data);
      setPendingSubmissions(subsRes.data || []);
      // Counted separately: a head-count query, so the tile can carry a badge
      // without pulling every listing into the dashboard.
      setPendingJobs(await educationJobs.countPending());

      // Calculate revenue data
      const bannerRequests = bannerRes.data || [];
      const paymentRequests = paymentReqRes.data || [];

      // Active/verified banner ad revenue
      const activeBannerRevenue = bannerRequests
        .filter(r => r.status === 'active')
        .reduce((sum, r) => sum + (r.final_price || 0), 0);

      // Verified listing payments (subscription revenue)
      const listingRevenue = paymentRequests
        .filter(r => r.status === 'verified')
        .reduce((sum, r) => sum + (r.amount || 0), 0);

      // Pending requests counts
      const pendingBannerCount = bannerRequests
        .filter(r => ['pending', 'payment_submitted'].includes(r.status)).length;
      const pendingPaymentCount = paymentRequests
        .filter(r => r.status === 'pending').length;

      setRevenueData({
        listingRevenue,
        bannerRevenue: activeBannerRevenue,
        pendingListingRequests: pendingPaymentCount,
        pendingBannerRequests: pendingBannerCount
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setPage('home');
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-brand-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Shield className="w-8 h-8" />Admin Dashboard
            </h2>
            <p className="text-gray-300 mt-1">Welcome, {profile?.full_name || profile?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadDashboard} className="p-2 bg-white/10 rounded-lg hover:bg-white/20">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={handleSignOut} className="flex items-center gap-2 px-4 py-2 bg-red-500 rounded-lg hover:bg-red-600">
              <LogOut className="w-4 h-4" />Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {[
          { label: 'Total Users', value: stats?.totalUsers || 0, icon: Users, color: 'blue' },
          { label: 'Total Listings', value: stats?.totalServiceProviders || 0, icon: Building2, color: 'purple' },
          { label: 'Pending Submissions', value: stats?.pendingSubmissions || 0, icon: FileText, color: 'red' }
        ].map((stat, i) => (
          <div key={i} className={`bg-white rounded-xl p-5 shadow-lg border-l-4 border-${stat.color}-500`}>
            <stat.icon className={`w-8 h-8 text-${stat.color}-500 mb-2`} />
            <p className="text-3xl font-black">{stat.value}</p>
            <p className="text-gray-500 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Quick Actions - Analytics & Ads */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <button onClick={() => setPage('admin-notifications')} className="bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white p-5 rounded-xl shadow-lg text-left relative">
          <Bell className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Notifications</h4>
          <p className="text-sm opacity-80">Email alerts & logs</p>
        </button>
        <button onClick={() => setPage('admin-analytics')} className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white p-5 rounded-xl shadow-lg text-left">
          <BarChart3 className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Analytics Dashboard</h4>
          <p className="text-sm opacity-80">Reports & insights</p>
        </button>
        <button onClick={() => setPage('admin-ads')} className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-5 rounded-xl shadow-lg text-left">
          <Image className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Banner Ads</h4>
          <p className="text-sm opacity-80">Manage advertisements</p>
        </button>
        <button onClick={() => setPage('admin-hero')} className="bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white p-5 rounded-xl shadow-lg text-left">
          <Play className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Hero Carousel</h4>
          <p className="text-sm opacity-80">Homepage slides</p>
        </button>
        <button onClick={() => setPage('admin-corrections')} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white p-5 rounded-xl shadow-lg text-left">
          <AlertCircle className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Data Corrections</h4>
          <p className="text-sm opacity-80">Community reports</p>
        </button>
        <button onClick={() => setPage('admin-contact')} className="bg-gradient-to-r from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 text-white p-5 rounded-xl shadow-lg text-left">
          <MessageSquare className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Contact Messages</h4>
          <p className="text-sm opacity-80">Form submissions</p>
        </button>
        <button onClick={() => setPage('admin-settings')} className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white p-5 rounded-xl shadow-lg text-left">
          <Settings className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Settings</h4>
          <p className="text-sm opacity-80">GA4, analytics config</p>
        </button>
      </div>

      {/* School Data Management */}
      <div className="bg-gradient-to-r from-brand-500 to-brand-500 rounded-2xl p-4 text-white">
        <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
          <GraduationCap className="w-6 h-6" /> School Data Management
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <button onClick={() => setPage('admin-schools')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <School className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Manage Schools</h4>
            <p className="text-xs opacity-80">Add, edit, delete</p>
          </button>
          <button onClick={() => setPage('admin-exam-results')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <Award className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Exam Results</h4>
            <p className="text-xs opacity-80">CSEC, CAPE, PEP</p>
          </button>
          <button onClick={() => setPage('admin-student-engagement')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <Star className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Student Resources</h4>
            <p className="text-xs opacity-80">Jobs, activities, exams</p>
          </button>
          <button onClick={() => setPage('admin-disruptions')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <CloudRain className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Disruption Events</h4>
            <p className="text-xs opacity-80">Hurricanes, etc.</p>
          </button>
          <button onClick={() => setPage('admin-suggestions')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <Edit className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Data Suggestions</h4>
            <p className="text-xs opacity-80">Community edits</p>
          </button>
          <button onClick={() => setPage('admin-reviews')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <MessageSquare className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">School Reviews</h4>
            <p className="text-xs opacity-80">Approve reviews</p>
          </button>
          <button onClick={() => setPage('admin-jobs')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left relative">
            <Briefcase className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Job Listings</h4>
            <p className="text-xs opacity-80">Approve vacancies</p>
            {pendingJobs > 0 && (
              <span className="absolute top-2 right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">
                {pendingJobs}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Quick Actions - Content Management */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button onClick={() => setPage('admin-listings')} className="bg-gradient-to-br from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white p-5 rounded-xl shadow-lg text-left">
          <Building2 className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Manage Services</h4>
          <p className="text-sm opacity-80">Tutors, Schools, Services</p>
        </button>
        <button onClick={() => setPage('admin-submissions')} className="bg-red-500 hover:bg-red-600 text-white p-5 rounded-xl shadow-lg text-left relative">
          <FileText className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Submissions</h4>
          <p className="text-sm opacity-80">Review requests</p>
          {stats?.pendingSubmissions > 0 && (
            <span className="absolute top-3 right-3 bg-white text-red-600 font-bold px-2 py-1 rounded-full text-sm">
              {stats.pendingSubmissions}
            </span>
          )}
        </button>
        <button onClick={() => setPage('admin-users')} className="bg-blue-500 hover:bg-blue-600 text-white p-5 rounded-xl shadow-lg text-left">
          <Users className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Manage Users</h4>
          <p className="text-sm opacity-80">View, roles</p>
        </button>
        <button onClick={() => setPage('admin-provider-reports')} className="bg-orange-500 hover:bg-orange-600 text-white p-5 rounded-xl shadow-lg text-left">
          <AlertTriangle className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Provider Reports</h4>
          <p className="text-sm opacity-80">Flags & complaints</p>
        </button>
        <button onClick={() => setPage('admin-provider-reviews')} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white p-5 rounded-xl shadow-lg text-left">
          <Star className="w-8 h-8 mb-2" />
          <h4 className="font-bold">Provider Reviews</h4>
          <p className="text-sm opacity-80">Moderate star ratings</p>
        </button>
      </div>

      {/* Revenue & Subscriptions - Service Provider Management */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl p-4 text-white">
        <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
          <DollarSign className="w-6 h-6" /> Revenue & Subscriptions
        </h3>

        {/* Revenue Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-sm opacity-80">Listing Revenue</p>
            <p className="text-2xl font-black">J${revenueData.listingRevenue.toLocaleString()}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-sm opacity-80">Banner Ad Revenue</p>
            <p className="text-2xl font-black">J${revenueData.bannerRevenue.toLocaleString()}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-sm opacity-80">Total Revenue</p>
            <p className="text-2xl font-black">J${(revenueData.listingRevenue + revenueData.bannerRevenue).toLocaleString()}</p>
          </div>
          <div className="bg-white/20 rounded-xl p-3 text-center">
            <p className="text-sm opacity-80">Pending Requests</p>
            <p className="text-2xl font-black">{revenueData.pendingListingRequests + revenueData.pendingBannerRequests}</p>
            <p className="text-xs opacity-70">{revenueData.pendingListingRequests} listings, {revenueData.pendingBannerRequests} ads</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button onClick={() => setPage('admin-subscriptions')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <CreditCard className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Subscriptions</h4>
            <p className="text-xs opacity-80">Billing & status</p>
          </button>
          <button onClick={() => setPage('admin-payments')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <Receipt className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Payments</h4>
            <p className="text-xs opacity-80">Record & history</p>
          </button>
          <button onClick={() => setPage('admin-payment-requests')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left relative">
            <Upload className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Payment Requests</h4>
            <p className="text-xs opacity-80">Review proof uploads</p>
            {revenueData.pendingListingRequests > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {revenueData.pendingListingRequests}
              </span>
            )}
          </button>
          <button onClick={() => setPage('admin-ads')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left relative">
            <Image className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Banner Ads</h4>
            <p className="text-xs opacity-80">Ad requests & revenue</p>
            {revenueData.pendingBannerRequests > 0 && (
              <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full">
                {revenueData.pendingBannerRequests}
              </span>
            )}
          </button>
          <button onClick={() => setPage('admin-email-templates')} className="bg-white/20 hover:bg-white/30 p-4 rounded-xl text-left">
            <Mail className="w-6 h-6 mb-1" />
            <h4 className="font-bold text-sm">Email Templates</h4>
            <p className="text-xs opacity-80">Customize notifications</p>
          </button>
        </div>
      </div>

      {/* Pending Submissions */}
      {pendingSubmissions.length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-red-500 text-white p-4">
            <h3 className="font-bold flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />Pending Submissions ({pendingSubmissions.length})
            </h3>
          </div>
          <div className="divide-y">
            {pendingSubmissions.slice(0, 5).map((sub) => (
              <div key={sub.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div>
                  <h4 className="font-bold">{sub.business_name}</h4>
                  <p className="text-sm text-gray-500">{sub.business_type} • {sub.contact_email}</p>
                </div>
                <button onClick={() => setPage('admin-submissions')} className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200">
                  Review
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <button onClick={() => setPage('home')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to App
      </button>
    </div>
  );
};

// ==================== ADMIN SUBMISSIONS PAGE ====================
export const AdminSubmissionsPage = ({ setPage }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data } = await db.submissions.getAll(filter === 'all' ? null : filter);
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this submission?')) return;
    try {
      await db.submissions.approve(id);
      loadSubmissions();
      setSelected(null);
    } catch (error) {
      console.error('Error approving:', error);
    }
  };

  const handleReject = async (id) => {
    if (!window.confirm('Reject this submission?')) return;
    try {
      await db.submissions.reject(id, rejectReason);
      loadSubmissions();
      setSelected(null);
      setRejectReason('');
    } catch (error) {
      console.error('Error rejecting:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission permanently?')) return;
    try {
      await db.submissions.delete(id);
      loadSubmissions();
      setSelected(null);
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-brand-100 text-brand-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <FileText className="w-8 h-8" />Business Submissions
            </h2>
            <p className="text-red-100 mt-1">Review and manage "Get Listed" requests</p>
          </div>
          <button onClick={loadSubmissions} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-medium capitalize ${filter === f ? 'bg-red-600 text-white' : 'bg-white text-gray-700 border'}`}
          >
            {f}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Submissions</h3>
          <p className="text-gray-400 mt-1">No {filter !== 'all' && filter} submissions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => (
            <div key={sub.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-bold text-lg">{sub.business_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(sub.status)}`}>
                        {sub.status}
                      </span>
                    </div>
                    <p className="text-gray-500 mt-1 capitalize">{sub.business_type}</p>
                  </div>
                  <button
                    onClick={() => setSelected(selected === sub.id ? null : sub.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {selected === sub.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>

                {selected === sub.id && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Contact Name</p>
                        <p className="font-medium">{sub.contact_name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{sub.contact_email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Phone</p>
                        <p className="font-medium">{sub.contact_phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Parish</p>
                        <p className="font-medium">{sub.parish || 'N/A'}</p>
                      </div>
                    </div>
                    {sub.website && (
                      <div>
                        <p className="text-sm text-gray-500">Website</p>
                        <a href={sub.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{sub.website}</a>
                      </div>
                    )}
                    {sub.description && (
                      <div>
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="text-gray-700">{sub.description}</p>
                      </div>
                    )}
                    {sub.services?.length > 0 && (
                      <div>
                        <p className="text-sm text-gray-500">Services</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {sub.services.map((s, i) => (
                            <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                              {typeof s === 'object' ? (s.label || s.value || JSON.stringify(s)) : s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <p className="text-xs text-gray-400">Submitted: {new Date(sub.created_at).toLocaleString()}</p>

                    {sub.status === 'pending' && (
                      <div className="pt-4 border-t space-y-3">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleApprove(sub.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl"
                          >
                            <CheckCircle className="w-5 h-5" />Approve
                          </button>
                          <button
                            onClick={() => handleReject(sub.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl"
                          >
                            <XCircle className="w-5 h-5" />Reject
                          </button>
                        </div>
                        <input
                          type="text"
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder="Rejection reason (optional)"
                          className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:outline-none"
                        />
                      </div>
                    )}

                    {sub.status !== 'pending' && (
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium"
                      >
                        <Trash2 className="w-4 h-4" />Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN USERS PAGE ====================
export const AdminUsersPage = ({ setPage }) => {
  const { profile: currentProfile } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await db.profiles.getAll();
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentProfile?.id) {
      alert("You cannot change your own role");
      return;
    }
    if (!window.confirm(`Change user role to ${newRole}?`)) return;
    try {
      await db.profiles.setRole(userId, newRole);
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleSuspend = async (user) => {
    const action = user.is_suspended ? 'unsuspend' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} ${user.full_name || user.email}?`)) return;
    try {
      await db.profiles.suspend(user.id, !user.is_suspended);
      loadUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to DELETE ${user.full_name || user.email}? This cannot be undone.`)) return;
    if (!window.confirm(`FINAL WARNING: This will permanently delete the user profile. Continue?`)) return;
    try {
      await db.profiles.delete(user.id);
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error: ' + error.message);
    }
  };

  const canManageUser = (user) => {
    // Can't manage yourself
    if (user.id === currentProfile?.id) return false;
    // Super admins can manage everyone except themselves
    if (currentProfile?.role === 'super_admin') return true;
    // Admins can manage regular users only
    if (currentProfile?.role === 'admin' && user.role === 'user') return true;
    return false;
  };

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Users className="w-8 h-8" />User Management
        </h2>
        <p className="text-blue-100 mt-1">View and manage user accounts and roles</p>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search users..."
          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">User</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Role</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Joined</th>
                <th className="px-4 py-3 text-left text-sm font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredUsers.map((user) => (
                <tr key={user.id} className={`hover:bg-gray-50 ${user.is_suspended ? 'bg-red-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {user.full_name || 'No name'}
                          {user.is_suspended && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded">SUSPENDED</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    {canManageUser(user) && (
                      <div className="flex items-center gap-2">
                        {/* Role Change - Super Admin only */}
                        {currentProfile?.role === 'super_admin' && (
                          <select
                            value={user.role}
                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                            className="px-2 py-1 border rounded-lg text-sm"
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                            <option value="super_admin">Super Admin</option>
                          </select>
                        )}
                        {/* Suspend/Unsuspend */}
                        <button
                          onClick={() => handleSuspend(user)}
                          className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                            user.is_suspended
                              ? 'bg-brand-100 text-brand-700 hover:bg-brand-200'
                              : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                          }`}
                          title={user.is_suspended ? 'Unsuspend User' : 'Suspend User'}
                        >
                          {user.is_suspended ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        </button>
                        {/* Delete - Super Admin only */}
                        {currentProfile?.role === 'super_admin' && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="p-2 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    )}
                    {user.id === currentProfile?.id && (
                      <span className="text-xs text-gray-400 italic">You</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== GENERIC DIRECTORY MANAGEMENT ====================
export const AdminDirectoryPage = ({ setPage, type, hideHeader = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, inactive
  const [tierFilter, setTierFilter] = useState('all'); // all, free, basic, premium
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);

  // Standard fields for service providers
  const serviceProviderFields = [
    { name: 'name', label: 'Business Name', type: 'text', required: true },
    { name: 'tier', label: 'Tier', type: 'tier' },
    { name: 'logo_url', label: 'Logo', type: 'logo' },
    { name: 'phone', label: 'Phone', type: 'tel' },
    { name: 'email', label: 'Email', type: 'email' },
    { name: 'website', label: 'Website', type: 'url' },
    { name: 'parish', label: 'Parish', type: 'parish' },
    { name: 'address', label: 'Address', type: 'text' },
    { name: 'hours', label: 'Business Hours', type: 'text' },
    { name: 'services', label: 'Services (comma separated)', type: 'text' },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'is_active', label: 'Active', type: 'toggle' }
  ];

  // All provider types now use the unified service_providers table
  const config = {
    all: {
      title: 'All Service Providers',
      icon: Building2,
      color: 'violet',
      db: {
        getAll: async () => db.serviceProviders.getAllAdmin(),
        create: async (data) => db.serviceProviders.create(data),
        update: db.serviceProviders.update,
        delete: db.serviceProviders.delete
      },
      bucket: 'business-logos',
      fields: [
        { name: 'name', label: 'Business Name', type: 'text', required: true },
        { name: 'type', label: 'Category', type: 'select', options: [
          { value: 'tutor', label: 'General Tutor' },
          { value: 'math_tutor', label: 'Math Tutor' },
          { value: 'science_tutor', label: 'Science Tutor' },
          { value: 'language_tutor', label: 'Language Tutor' },
          { value: 'test_prep', label: 'Exam Prep Centre' },
          { value: 'homework_helper', label: 'Homework Helper' },
          { value: 'consultant', label: 'Education Consultant' },
          { value: 'school_supplies', label: 'School Supplies' },
          { value: 'bookshop', label: 'Bookshop' },
          { value: 'uniforms', label: 'Uniform Supplier' },
          { value: 'private_school', label: 'Private School' },
          { value: 'other', label: 'Other' }
        ]},
        { name: 'contact_name', label: 'Contact Name', type: 'text' },
        { name: 'tier', label: 'Tier', type: 'tier' },
        { name: 'logo_url', label: 'Logo', type: 'logo' },
        { name: 'phone', label: 'Phone', type: 'tel' },
        { name: 'email', label: 'Email', type: 'email' },
        { name: 'website', label: 'Website', type: 'url' },
        { name: 'parish', label: 'Parish', type: 'parish' },
        { name: 'address', label: 'Address', type: 'text' },
        { name: 'hours', label: 'Business Hours', type: 'text' },
        { name: 'services', label: 'Services (comma separated)', type: 'text' },
        { name: 'description', label: 'Description', type: 'textarea' },
        { name: 'is_active', label: 'Active', type: 'toggle' }
      ]
    },
    other: {
      title: 'Other Services',
      icon: Store,
      color: 'gray',
      db: {
        getAll: async () => db.serviceProviders.getByType('other'),
        create: async (data) => db.serviceProviders.create({ ...data, type: 'other' }),
        update: db.serviceProviders.update,
        delete: db.serviceProviders.delete
      },
      bucket: 'business-logos',
      fields: serviceProviderFields
    }
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File too large. Maximum 5MB.');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file.');
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${type}-${Date.now()}.${fileExt}`;
      const filePath = `${type}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(cfg.bucket)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from(cfg.bucket)
        .getPublicUrl(filePath);

      setFormData({ ...formData, logo_url: urlData.publicUrl });
      setLogoPreview(urlData.publicUrl);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const cfg = config[type];
  const Icon = cfg.icon;

  useEffect(() => {
    loadItems();
  }, [type]);

  const loadItems = async () => {
    setLoading(true);
    try {
      const { data } = await cfg.db.getAll();
      setItems(data || []);
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await cfg.db.create(formData);
      } else {
        await cfg.db.update(editing, formData);
      }
      loadItems();
      setEditing(null);
      setFormData({});
    } catch (error) {
      console.error('Error saving:', error);
      alert('Error saving: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await cfg.db.delete(id);
      loadItems();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const toggleActive = async (item) => {
    try {
      await cfg.db.update(item.id, { is_active: !item.is_active });
      loadItems();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setFormData(item);
    setLogoPreview(item.logo_url || null);
  };

  const startNew = () => {
    setEditing('new');
    setFormData({ is_active: true, tier: 'free' });
    setLogoPreview(null);
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(search.toLowerCase()) ||
      item.email?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && item.is_active) ||
      (statusFilter === 'inactive' && !item.is_active);
    const matchesTier = tierFilter === 'all' || item.tier === tierFilter;
    return matchesSearch && matchesStatus && matchesTier;
  });

  return (
    <div className="space-y-6">
      {!hideHeader ? (
        <div className={`bg-gradient-to-r from-${cfg.color}-600 to-${cfg.color}-700 text-white rounded-2xl p-6 shadow-xl`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black flex items-center gap-3">
                <Icon className="w-8 h-8" />{cfg.title}
              </h2>
              <p className={`text-${cfg.color}-100 mt-1`}>Manage directory listings</p>
            </div>
            <button onClick={startNew} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
              <Plus className="w-5 h-5" />Add New
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-700 flex items-center gap-2">
            <Icon className={`w-6 h-6 text-${cfg.color}-600`} />{cfg.title}
          </h3>
          <button onClick={startNew} className={`flex items-center gap-2 px-4 py-2 bg-${cfg.color}-600 text-white rounded-lg hover:bg-${cfg.color}-700`}>
            <Plus className="w-5 h-5" />Add New {type === 'all' ? '' : type.slice(0, -1)}
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            />
          </div>
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {/* Tier Filter */}
          <select
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none bg-white"
          >
            <option value="all">All Tiers</option>
            <option value="free">Free</option>
            <option value="basic">Basic</option>
            <option value="premium">Premium</option>
          </select>
        </div>
      </div>

      {/* Edit Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto">
            <div className={`bg-${cfg.color}-600 text-white p-4 rounded-t-2xl`}>
              <h3 className="font-bold">{editing === 'new' ? 'Add New Entry' : 'Edit Entry'}</h3>
            </div>
            <div className="p-6 space-y-4">
              {cfg.fields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label} {field.required && <span className="text-red-500">*</span>}
                  </label>

                  {/* Logo Upload */}
                  {field.type === 'logo' ? (
                    <div className="space-y-2">
                      {(logoPreview || formData.logo_url) && (
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                          <img
                            src={logoPreview || formData.logo_url}
                            alt="Logo preview"
                            className="w-16 h-16 object-contain rounded-lg bg-white border"
                          />
                          <button
                            type="button"
                            onClick={() => { setLogoPreview(null); setFormData({ ...formData, logo_url: '' }); }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 text-center hover:border-blue-400 transition-colors">
                        <label className="cursor-pointer">
                          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600">{uploading ? 'Uploading...' : 'Click to upload logo'}</p>
                          <input
                            type="file"
                            onChange={handleLogoUpload}
                            accept="image/*"
                            className="hidden"
                            disabled={uploading}
                          />
                        </label>
                      </div>
                    </div>
                  ) : field.type === 'tier' ? (
                    <select
                      value={formData[field.name] || 'free'}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    >
                      {TIER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : field.type === 'parish' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Select Parish...</option>
                      {JAMAICA_PARISHES.map(parish => (
                        <option key={parish} value={parish}>{parish}</option>
                      ))}
                    </select>
                  ) : field.type === 'toggle' ? (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, [field.name]: !formData[field.name] })}
                      className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                        formData[field.name] ? 'bg-brand-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                          formData[field.name] ? 'translate-x-7' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      rows={3}
                    />
                  ) : field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                    >
                      <option value="">Select...</option>
                      {field.options.map((opt, idx) => (
                        <option key={idx} value={typeof opt === 'object' ? opt.value : opt}>
                          {typeof opt === 'object' ? opt.label : opt}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                      required={field.required}
                    />
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={uploading}
                  className={`flex-1 py-3 bg-${cfg.color}-600 hover:bg-${cfg.color}-700 text-white font-bold rounded-xl disabled:opacity-50`}
                >
                  {uploading ? 'Uploading...' : 'Save'}
                </button>
                <button
                  onClick={() => { setEditing(null); setFormData({}); setLogoPreview(null); }}
                  className="flex-1 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Items List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <Icon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Entries</h3>
          <p className="text-gray-400 mt-1">Add your first entry to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredItems.map((item) => (
            <div key={item.id} className={`bg-white rounded-xl shadow-lg p-5 ${item.is_active === false ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  {item.logo_url ? (
                    <img src={item.logo_url} alt="" className="w-16 h-16 object-contain rounded-lg bg-gray-50 border" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Building2 className="w-8 h-8 text-gray-300" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-lg">{item.name}</h3>
                      {/* Tier Badge */}
                      {item.tier && item.tier !== 'free' && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          item.tier === 'premium' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {item.tier}
                        </span>
                      )}
                      {item.is_active === false && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 ${
                          item.tier && item.tier !== 'free' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          <Clock className="w-3 h-3" />
                          {item.tier && item.tier !== 'free' ? 'Awaiting Payment' : 'Inactive'}
                        </span>
                      )}
                    </div>
                    {item.firm && <p className="text-gray-500">{item.firm}</p>}
                    {item.parish && <p className="text-gray-500 text-sm">{item.parish}</p>}
                    <div className="flex items-center gap-4 mt-1">
                      {item.phone && <p className="text-sm text-gray-500 flex items-center gap-1"><Phone className="w-3 h-3" />{item.phone}</p>}
                      {item.email && <p className="text-sm text-gray-500 flex items-center gap-1"><Mail className="w-3 h-3" />{item.email}</p>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => toggleActive(item)}
                    className={`p-2 rounded-lg ${item.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-brand-600 hover:bg-brand-50'}`}
                    title={item.is_active ? 'Deactivate Listing' : (item.tier && item.tier !== 'free' ? 'Payment Received - Make Live' : 'Activate Listing')}
                  >
                    {item.is_active ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => setPage('admin-subscriptions')}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Manage Subscriptions"
                  >
                    <CreditCard className="w-5 h-5" />
                  </button>
                  <button onClick={() => startEdit(item)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit">
                    <Edit className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!hideHeader && (
        <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />Back to Dashboard
        </button>
      )}
    </div>
  );
};

// ==================== ADMIN ALL LISTINGS PAGE (Unified) ====================
export const AdminAllListingsPage = ({ setPage }) => {
  const [selectedType, setSelectedType] = useState('all');

  const listingTypes = [
    { key: 'all', label: 'Show All', icon: Building2, color: 'violet' },
    { key: 'other', label: 'Other', icon: Store, color: 'gray' }
  ];

  return (
    <div className="space-y-6">
      {/* Header with Type Selector */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Building2 className="w-8 h-8" />Manage All Listings
            </h2>
            <p className="text-violet-200 mt-1">All service provider categories</p>
          </div>
          <button onClick={() => setPage('admin')} className="flex items-center gap-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30">
            <ArrowLeft className="w-5 h-5" />Back
          </button>
        </div>

        {/* Type Tabs */}
        <div className="flex flex-wrap gap-2">
          {listingTypes.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSelectedType(key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl font-medium transition-all text-sm ${
                selectedType === key
                  ? 'bg-white text-violet-700 shadow-lg'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Directory Content */}
      <AdminDirectoryPage setPage={setPage} type={selectedType} hideHeader={true} />
    </div>
  );
};

// ==================== ADMIN SETTINGS PAGE ====================
export const AdminSettingsPage = ({ setPage }) => {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white rounded-2xl p-6 shadow-xl">
        <h2 className="text-2xl font-black flex items-center gap-3">
          <Settings className="w-8 h-8" />App Settings
        </h2>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="font-bold mb-4">API Keys Configuration</h3>
        <p className="text-gray-600 mb-4">
          API keys are configured via environment variables in Vercel. Go to your Vercel project settings to manage:
        </p>
        <ul className="space-y-2 text-sm">
          <li className="flex items-center gap-2">
            <code className="bg-gray-100 px-2 py-1 rounded">REACT_APP_SUPABASE_URL</code>
            <span className="text-gray-500">- Supabase project URL</span>
          </li>
          <li className="flex items-center gap-2">
            <code className="bg-gray-100 px-2 py-1 rounded">REACT_APP_SUPABASE_ANON_KEY</code>
            <span className="text-gray-500">- Supabase anonymous key</span>
          </li>
          <li className="flex items-center gap-2">
            <code className="bg-gray-100 px-2 py-1 rounded">REACT_APP_GOOGLE_MAPS_API_KEY</code>
            <span className="text-gray-500">- Google Maps API key (optional)</span>
          </li>
        </ul>
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
        <h4 className="font-bold text-blue-900">Supabase Dashboard</h4>
        <p className="text-blue-700 mt-1">
          For advanced database management, storage configuration, and user authentication settings,
          visit your Supabase dashboard.
        </p>
        <a
          href="https://supabase.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Globe className="w-4 h-4" />Open Supabase Dashboard
        </a>
      </div>

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN CONTACT SUBMISSIONS ====================
export const AdminContactSubmissions = ({ setPage }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      const { data } = await contactSubmissions.getAll(filter === 'all' ? null : filter);
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error loading contact submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await contactSubmissions.updateStatus(id, newStatus, adminNotes || null);
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: newStatus, admin_notes: adminNotes } : s));
      setSelectedSubmission(null);
      setAdminNotes('');
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact submission?')) return;
    try {
      await contactSubmissions.delete(id);
      setSubmissions(prev => prev.filter(s => s.id !== id));
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      new: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'New' },
      read: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Read' },
      responded: { bg: 'bg-brand-100', text: 'text-brand-700', label: 'Responded' },
      archived: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Archived' }
    };
    const config = configs[status] || configs.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-JM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-700 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <MessageSquare className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-black">Contact Submissions</h2>
              <p className="text-brand-100">{submissions.length} {filter === 'all' ? 'total' : filter} message{submissions.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={loadSubmissions} className="p-2 bg-white/10 rounded-lg hover:bg-white/20">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-lg flex items-center gap-3 flex-wrap">
        <Filter className="w-5 h-5 text-gray-400" />
        {['new', 'read', 'responded', 'archived', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
              filter === f
                ? 'bg-brand-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Submissions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-brand-600 animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-lg">
          <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">No Messages</h3>
          <p className="text-gray-500 mt-2">No {filter !== 'all' && filter} contact submissions found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((sub) => (
            <div key={sub.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusBadge(sub.status)}
                      <span className="text-sm text-gray-500 capitalize">{sub.category?.replace('_', ' ') || 'General'}</span>
                      <span className="text-xs text-gray-400">{formatDate(sub.created_at)}</span>
                    </div>
                    <h4 className="font-bold text-lg text-gray-900">{sub.subject}</h4>
                    <p className="text-gray-600 mt-1">{sub.message}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="mt-4 p-3 bg-gray-50 rounded-lg flex flex-wrap gap-4 text-sm">
                  <p className="flex items-center gap-1">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{sub.name}</span>
                  </p>
                  <a href={`mailto:${sub.email}`} className="flex items-center gap-1 text-brand-600 hover:underline">
                    <Mail className="w-4 h-4" />
                    {sub.email}
                  </a>
                  {sub.phone && (
                    <a href={`tel:${sub.phone}`} className="flex items-center gap-1 text-brand-600 hover:underline">
                      <Phone className="w-4 h-4" />
                      {sub.phone}
                    </a>
                  )}
                </div>

                {/* Admin Notes */}
                {sub.admin_notes && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-800"><span className="font-medium">Admin Notes:</span> {sub.admin_notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  {sub.status === 'new' && (
                    <button
                      onClick={() => handleStatusChange(sub.id, 'read')}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
                    >
                      <Eye className="w-4 h-4" />Mark as Read
                    </button>
                  )}
                  <a
                    href={`mailto:${sub.email}?subject=Re: ${encodeURIComponent(sub.subject)}`}
                    onClick={() => setTimeout(() => handleStatusChange(sub.id, 'responded'), 500)}
                    className="px-4 py-2 bg-brand-100 text-brand-700 rounded-lg font-medium hover:bg-brand-200 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />Reply via Email
                  </a>
                  <button
                    onClick={() => setSelectedSubmission(sub)}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg font-medium hover:bg-blue-200 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />Update Status
                  </button>
                  {sub.status !== 'archived' && (
                    <button
                      onClick={() => handleStatusChange(sub.id, 'archived')}
                      className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
                    >
                      <EyeOff className="w-4 h-4" />Archive
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(sub.id)}
                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Status Update Modal */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b">
              <h3 className="text-xl font-bold">Update Status</h3>
              <p className="text-gray-500 mt-1">Update the status for "{selectedSubmission.subject}"</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {['new', 'read', 'responded', 'archived'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setSelectedSubmission({ ...selectedSubmission, newStatus: status })}
                      className={`px-4 py-2 rounded-lg font-medium capitalize transition-colors ${
                        selectedSubmission.newStatus === status
                          ? 'bg-brand-500 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Admin Notes (optional)</label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes about this contact..."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                  rows={3}
                />
              </div>
            </div>
            <div className="p-6 border-t flex items-center justify-end gap-3">
              <button
                onClick={() => { setSelectedSubmission(null); setAdminNotes(''); }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={() => handleStatusChange(selectedSubmission.id, selectedSubmission.newStatus || selectedSubmission.status)}
                className="px-6 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};
